import React, { useState } from 'react';
import {
  Box,
  theme,
  Flex,
  Text,
  Link,
  Label,
  Input,
  FormMessage,
  Select,
} from '@xometry/xometry_loft';
import TooltipBlueIcon from '../../../../../components/TooltipBlueIcon';
import { states } from '../../../../../utils/constants';
import { css } from '@emotion/react';

const BusinessInformation = ({
  values,
  handleChange,
  handleBlur,
  touched,
  errors,
  businessInformationEditMode,
  setBusinessInformationEditMode,
}) => {
  const [showStreet2, setShowStreet2] = useState(false);
  const stateOptions: any[] = [];
  const keys = Object.keys(states);

  // eslint-disable-next-line
  for (const key of keys) {
    stateOptions.push(
      <option key={key} value={states[key]}>
        {states[key]}
      </option>
    );
  }

  const invalidStreetName = Boolean(
    (errors.street1 && touched.street1) || (errors.street2 && touched.street2)
  );
  const invalidCity = Boolean(errors.city && touched.city);
  const invalidState = Boolean(errors.state && touched.state);
  const invalidZipCode = Boolean(errors.zipcode && touched.zipcode);

  return (
    <Box
      borderBottom={`1px solid ${theme.colors.darkBlues[25]}`}
      marginBottom="28px"
    >
      <Box marginBottom="16px">
        <Text kind="body-title">Business Information</Text>
      </Box>
      <Flex marginBottom="4px" justifyContent="space-between">
        <Text
          kind="body-sm-title"
          marginBottom="4px"
          fontWeight={theme.fontWeights.semibold}
        >
          Business Address
        </Text>
        <Link
          onClick={() =>
            setBusinessInformationEditMode(!businessInformationEditMode)
          }
        >
          {!businessInformationEditMode ? 'Edit' : 'Save Changes'}
        </Link>
      </Flex>
      {!businessInformationEditMode && (
        <Flex flexDirection="column" marginBottom="16px">
          <Text kind="body-sm" marginBottom="4px">
            {values.street1}
          </Text>
          {values.street2 && (
            <Text kind="body-sm" marginBottom="4px">
              {values.street2}
            </Text>
          )}
          <Text kind="body-sm" marginBottom="4px">
            {values.city} {values.state}
            {', '}
            {values.zipcode}
          </Text>
          <Text kind="body-sm" marginBottom="4px">
            Billing Address is Same as Business Address
          </Text>
        </Flex>
      )}
      {businessInformationEditMode && (
        <>
          <Box width="70%">
            <Label htmlFor="street1">Street</Label>
            <Input
              width="74%"
              className="pii-encrypt"
              placeholder="Line 1"
              id="street1"
              type="text"
              name="street1"
              onChange={handleChange}
              onBlur={handleBlur}
              value={values.street1}
              invalid={invalidStreetName}
              marginRight="8px"
            />
          </Box>
          <Link
            onClick={() => setShowStreet2(!showStreet2)}
            css={css`
              display: block;
              margin-top: 4px;
              &:hover {
                text-decoration: none;
              }
            `}
          >
            {showStreet2 ? (
              <i className="fa fa-minus" />
            ) : (
              <i className="fa fa-plus" />
            )}{' '}
            Add Apt, Suite, or Other
          </Link>
          {showStreet2 && (
            <Box width="70%">
              <Input
                width="74%"
                className="pii-encrypt"
                placeholder="Line 2"
                type="street2"
                name="street2"
                onChange={handleChange}
                onBlur={handleBlur}
                value={values.street2}
                invalid={invalidStreetName}
                margin="4px 0px"
              />
            </Box>
          )}
          {invalidStreetName && (
            <FormMessage invalid={invalidStreetName}>
              {errors.street2 || errors.street1}
            </FormMessage>
          )}
          <Flex width="70%" marginBottom="8px">
            <Box width="100%" marginRight="8px">
              <Label htmlFor="city">City</Label>
              <Input
                width="100%"
                className="pii-encrypt"
                id="city"
                name="city"
                value={values.city}
                onChange={handleChange}
                onBlur={handleBlur}
                invalid={invalidCity}
              />
              {invalidCity && (
                <FormMessage invalid={invalidCity}>{errors.city}</FormMessage>
              )}
            </Box>
            <Box width="50%" marginRight="8px">
              <Label htmlFor="state">State</Label>
              <Select
                width="100%"
                className="pii-encrypt"
                id="state"
                name="state"
                backgroundColor="white"
                onChange={handleChange}
                onBlur={handleBlur}
                invalid={invalidState}
                value={values.state}
              >
                <option value="">Select</option>
                {stateOptions}
              </Select>
              {invalidState && (
                <FormMessage invalid={invalidState}>{errors.state}</FormMessage>
              )}
            </Box>
            <Box width="50%">
              <Label htmlFor="zipcode">Zip Code</Label>
              <Input
                width="100%"
                className="pii-encrypt"
                type="zipcode"
                id="zipcode"
                name="zipcode"
                onChange={handleChange}
                onBlur={handleBlur}
                value={values.zipcode}
                invalid={invalidZipCode}
              />
              {invalidZipCode && (
                <FormMessage invalid={invalidZipCode}>
                  {errors.zipcode}
                </FormMessage>
              )}
            </Box>
          </Flex>
        </>
      )}
      <Box marginBottom="4px" width="70%">
        <Label htmlFor="invoiceEmail">Invoicing Email</Label>
        <Input
          width="49%"
          className="pii-encrypt"
          id="invoiceEmail"
          value={values.invoiceEmail}
          onChange={handleChange}
          onBlur={handleBlur}
          invalid={Boolean(touched.invoiceEmail && errors.invoiceEmail)}
        />
        {Boolean(touched.invoiceEmail && errors.invoiceEmail) && (
          <FormMessage invalid>{errors.invoiceEmail}</FormMessage>
        )}
      </Box>
      <Box marginBottom="16px">
        <Text kind="body-sm">
          Xometry Invoices will be sent to this email address.
        </Text>
      </Box>
      <Flex marginBottom="28px" width="70%">
        <Box marginRight="8px" width="100%">
          <Label htmlFor="taxId">
            <Flex>
              EIN/Tax ID
              <TooltipBlueIcon>
                Your Employer Identification Number (or Federal Tax ID) is a
                9-digit number which can typically be found on the CP 575 Form
                issued by the IRS. This information is required for your
                application.
              </TooltipBlueIcon>
            </Flex>
          </Label>
          <Input
            width="100%"
            className="pii-encrypt"
            id="taxId"
            type="text"
            data-testid="taxId"
            value={values.taxId}
            onChange={handleChange}
            onBlur={handleBlur}
            invalid={Boolean(touched.taxId && errors.taxId)}
          />
          {Boolean(touched.taxId && errors.taxId) && (
            <FormMessage invalid>{errors.taxId}</FormMessage>
          )}
        </Box>
        <Box width="100%">
          <Label htmlFor="duns">
            <Flex>
              <Box marginRight="4px">DUNS Number</Box>
              <Box fontWeight={theme.fontWeights.regular}>
                <i>Optional</i>
              </Box>
              <TooltipBlueIcon>
                This field is optional. A DUNS number is a unique 9-digit number
                that must be applied for that we use to evaluate credit. Not all
                businesses have a DUNS number.
              </TooltipBlueIcon>
            </Flex>
          </Label>
          <Input
            width="100%"
            className="pii-encrypt"
            id="duns"
            type="text"
            value={values.duns}
            onChange={handleChange}
            onBlur={handleBlur}
            invalid={Boolean(touched.duns && errors.duns)}
          />
          {Boolean(touched.duns && errors.duns) && (
            <FormMessage invalid>{errors.duns}</FormMessage>
          )}
        </Box>
      </Flex>
    </Box>
  );
};

export default BusinessInformation;
